import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  addDaysMs,
  formatAmount,
  formatDate,
  formatMoney,
  formatShortDay,
  isoDay,
  nowMs,
  parseIsoDay,
} from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Failed } from '../../ui/Load'
import { Pagination } from '../../ui/Pagination'
import { TableSkeleton } from '../../ui/Skeleton'
import { useOrders } from '../orders/api'
import { STATUS_TONE, statusText } from '../orders/model'
import { useProductSales } from './api'

/** «21 сент.»; подпись не дата — показываем как пришла. */
function dayLabel(label: string): string {
  const ms = parseIsoDay(label)
  return ms === null ? label : formatShortDay(ms)
}

/** Периоды вкладки «Продажи»: неделя, месяц, квартал. */
const SALES_PERIODS = [7, 30, 90] as const
type SalesPeriod = (typeof SALES_PERIODS)[number]
const ORDER_PAGE_SIZES = [10, 25, 50] as const

/** Продажи за выбранный период и заказы, в которых был товар. */
export function SalesTab({ id, withOrders }: { id: number; withOrders: boolean }) {
  const [now] = useState(() => nowMs())
  const [period, setPeriod] = useState<SalesPeriod>(30)
  const start = addDaysMs(now, -(period - 1))
  const sales = useProductSales(id, isoDay(start), isoDay(now), true)
  const days = sales.data?.byDay ?? []
  const sold = days.filter((d) => d.qty > 0).length
  const peak = Math.max(1, ...days.map((d) => d.qty))
  const first = days[0]
  const last = days[days.length - 1]
  // Пока идёт запрос — полоска на месте числа: строка не прыгает.
  const shown = (value: string | undefined) =>
    value === undefined ? <span className="ui-skeleton pd-skel" /> : value

  return (
    <div className="ui-stack">
      <div className="pd-period">
        <div className="ui-seg" role="group" aria-label="Период">
          {SALES_PERIODS.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={period === value}
              onClick={() => {
                setPeriod(value)
              }}
            >
              {value} дней
            </button>
          ))}
        </div>
        <span className="num">
          {formatDate(start)} — {formatDate(now)}
        </span>
      </div>

      {sales.isError ? (
        <Failed
          what="продажи"
          error={sales.error}
          onRetry={() => {
            void sales.refetch()
          }}
        />
      ) : (
        <section className="ui-card" aria-label="Продажи за период" aria-busy={sales.isPending}>
          <dl className="ui-stats">
            <div>
              <dt>Продано</dt>
              <dd>{shown(sales.data && `${formatAmount(sales.data.qty)} шт`)}</dd>
            </div>
            <div>
              <dt>Выручка без баллов</dt>
              <dd>{shown(sales.data && formatMoney(sales.data.revenue))}</dd>
            </div>
            <div>
              <dt>Средняя цена</dt>
              <dd>
                {shown(
                  sales.data &&
                    (sales.data.qty
                      ? formatMoney(Math.round(sales.data.revenue / sales.data.qty))
                      : '—'),
                )}
              </dd>
            </div>
            <div>
              <dt>Дней с продажами</dt>
              <dd>{shown(sales.data && `${sold} из ${days.length}`)}</dd>
            </div>
          </dl>
          <div className="pd-chart">
            {sales.data && sold === 0 ? (
              <p className="ui-muted-line">За период продаж не было</p>
            ) : null}
            {first && last && sold > 0 ? (
              <>
                {/* Столбики — картинка для глаз; те же числа для читалки —
                    таблицей ниже. */}
                <div className="pd-bars" aria-hidden="true">
                  <span className="pd-peak">{peak} шт</span>
                  {days.map((day) => (
                    <span
                      key={day.label}
                      className="col"
                      data-tip={`${dayLabel(day.label)}: ${day.qty} шт`}
                    >
                      <i style={{ height: `${Math.round((day.qty / peak) * 100)}%` }} />
                    </span>
                  ))}
                </div>
                <div className="pd-axis" aria-hidden="true">
                  <span>{dayLabel(first.label)}</span>
                  <span>{dayLabel(last.label)}</span>
                </div>
                <table className="visually-hidden">
                  <caption>Продажи по дням</caption>
                  <tbody>
                    {days.map((day) => (
                      <tr key={day.label}>
                        <th scope="row">{dayLabel(day.label)}</th>
                        <td>{day.qty} шт</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}
          </div>
        </section>
      )}

      {withOrders ? <ProductOrders id={id} /> : null}
    </div>
  )
}

/** Заказы с товаром — все, не только за период: у списка заказов нет фильтра по дате. */
function ProductOrders({ id }: { id: number }) {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<number>(ORDER_PAGE_SIZES[0])
  const orders = useOrders({ productId: id, page, pageSize: size }, true, true)

  return (
    <section className="ui-table-card" aria-labelledby="pd-orders">
      <div className="ui-card-head">
        <h3 id="pd-orders">
          Заказы с этим товаром
          {orders.data?.total ? <span className="ui-count">{orders.data.total}</span> : null}
        </h3>
      </div>
      {orders.isPending ? <TableSkeleton what="заказы" rows={3} /> : null}
      {orders.isError ? (
        <Failed
          what="заказы"
          error={orders.error}
          onRetry={() => {
            void orders.refetch()
          }}
        />
      ) : null}
      {orders.data?.items.length === 0 ? (
        <div className="ui-empty">
          <Icon name="i-box" />
          <b>Товар ещё не заказывали</b>
        </div>
      ) : null}
      {orders.data && orders.data.items.length > 0 ? (
        <>
          <div className="ui-table-wrap" aria-busy={orders.isPlaceholderData}>
            <table className="ui-table">
              <thead>
                <tr>
                  <th scope="col">Заказ</th>
                  <th scope="col">Дата</th>
                  <th scope="col">Клиент</th>
                  <th scope="col" data-align="end">
                    Кол-во
                  </th>
                  <th scope="col" data-align="end">
                    Сумма позиции
                  </th>
                  <th scope="col">Статус</th>
                </tr>
              </thead>
              <tbody>
                {orders.data.items.map((order) => {
                  const href = `/orders/${order.id}`
                  const line = order.lines.find((l) => l.productId === id)
                  return (
                    <tr
                      key={order.id}
                      data-href={href}
                      onClick={() => {
                        void navigate(href)
                      }}
                    >
                      <td>
                        <Link className="primary num" to={href}>
                          № {order.number}
                        </Link>
                      </td>
                      <td className="num">{formatDate(order.createdAt)}</td>
                      <td>{order.customerName}</td>
                      <td className="num" data-align="end">
                        {line ? line.qty : '—'}
                      </td>
                      <td className="num" data-align="end">
                        {line ? formatMoney(line.total) : '—'}
                      </td>
                      <td>
                        <span className="ui-status" data-tone={STATUS_TONE[order.status]}>
                          {statusText(order.status)}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            label="Страницы заказов"
            page={page}
            pageSize={size}
            total={orders.data.total}
            sizes={ORDER_PAGE_SIZES}
            busy={orders.isPlaceholderData}
            onPage={setPage}
            onPageSize={(next) => {
              setSize(next)
              setPage(1)
            }}
          />
        </>
      ) : null}
    </section>
  )
}
